import { useEffect, useState } from 'react';
import {
  deletePurchaseDepartmentPlanItem,
  getPurchaseDepartmentPlanDetails,
  updatePurchaseDepartmentPlanQuantity,
  type PurchaseDepartmentPlanDetailGroup,
  type PurchaseDepartmentPlanDetailItem,
  type PurchaseMonthDetailRow,
} from '@/api/purchase';
import ConfirmModal from '@/pages/purchase-plan/components/ConfirmModal';
import { showToast } from '@/utils/message';

interface MonthSummaryItemsModalProps {
  isOpen: boolean;
  month: string;
  source: PurchaseMonthDetailRow | null;
  onClose: () => void;
  onChanged?: () => void;
}

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function getFlowStatusTag(status?: number | string, statusText?: string) {
  const code = Number(status);
  const label = statusText || '-';
  if (code === 10) return { color: '#19be6b', label };
  if (code === 1) return { color: '#2d8cf0', label };
  if (code === 2) return { color: '#ed4014', label };
  if (code === 99) return { color: '#c5c8ce', label };
  return { color: '#33bcb7', label };
}

function itemKey(row: PurchaseDepartmentPlanDetailItem) {
  return String(row.id ?? '');
}

export default function MonthSummaryItemsModal({
  isOpen,
  month,
  source,
  onClose,
  onChanged,
}: MonthSummaryItemsModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hospital, setHospital] = useState<PurchaseDepartmentPlanDetailGroup | null>(null);
  const [groups, setGroups] = useState<PurchaseDepartmentPlanDetailGroup[]>([]);
  const [goodsTitle, setGoodsTitle] = useState('');
  const [specLabel, setSpecLabel] = useState('');
  const [unit, setUnit] = useState('');
  const [monthLabel, setMonthLabel] = useState('');
  const [totalPlan, setTotalPlan] = useState(0);
  const [totalPurchased, setTotalPurchased] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [editingId, setEditingId] = useState('');
  const [editQty, setEditQty] = useState(1);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PurchaseDepartmentPlanDetailItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen || !source) {
      setLoading(false);
      setError('');
      setHospital(null);
      setGroups([]);
      setGoodsTitle('');
      setSpecLabel('');
      setUnit('');
      setMonthLabel('');
      setTotalPlan(0);
      setTotalPurchased(0);
      setEditingId('');
      setPendingDelete(null);
      return;
    }

    const goodsId = n(source.goods_id);
    if (goodsId < 1) {
      setError('物资无效');
      setHospital(null);
      setGroups([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    void (async () => {
      try {
        const res = await getPurchaseDepartmentPlanDetails({
          month,
          goods_id: goodsId,
          spec_id: n(source.spec_id) > 0 ? n(source.spec_id) : 0,
        });
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        if (!ok) {
          setHospital(null);
          setGroups([]);
          setError(res.message || res.msg || '科室明细加载失败');
          return;
        }
        const hospitalGroup = res.data?.hospital;
        setHospital(
          hospitalGroup && Array.isArray(hospitalGroup.items) && hospitalGroup.items.length > 0
            ? hospitalGroup
            : null,
        );
        const list = Array.isArray(res.data?.departments) ? res.data.departments : [];
        setGroups(list);
        setGoodsTitle(String(res.data?.goods?.title || source.goods_name || '').trim());
        setSpecLabel(
          String(res.data?.goods?.spec_name || source.spec_name || '').trim() ||
            (n(source.spec_id) > 0 ? '' : '不限规格'),
        );
        setUnit(String(res.data?.goods?.unit || source.unit || '').trim());
        setMonthLabel(String(res.data?.apply_month_text || '').trim());
        setTotalPlan(n(res.data?.plan_quantity));
        setTotalPurchased(n(res.data?.purchased_quantity));
      } catch {
        if (!cancelled) {
          setHospital(null);
          setGroups([]);
          setError('网络异常，科室明细加载失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, month, reloadKey, source]);

  if (!isOpen) return null;

  const busy = saving || deleting;
  const qtySuffix = unit ? ` ${unit}` : '';

  const startEdit = (row: PurchaseDepartmentPlanDetailItem) => {
    if (busy) return;
    const id = itemKey(row);
    if (!id) return;
    setEditingId(id);
    setEditQty(Math.max(1, n(row.plan_quantity)));
  };

  const saveEdit = async (row: PurchaseDepartmentPlanDetailItem) => {
    const id = itemKey(row);
    if (!id || saving) return;
    const displayQty = Math.floor(Number(editQty) || 0);
    if (displayQty < 1) {
      showToast('计划数量须大于 0', { type: 'error' });
      return;
    }
    const ratio = Math.max(1, n(row.multiple_unit));
    if (row.folded && displayQty % ratio !== 0) {
      showToast(`计划数量须为换算倍数 ${ratio} 的整数倍`, { type: 'error' });
      return;
    }
    const isHospital = n(row.control_type) === 1;
    const minQty = Math.max(1, n(row.min_plan_quantity ?? row.net_used_quantity));
    if (displayQty < minQty) {
      showToast(
        isHospital
          ? `全院计划合计不可小于各科室已采购合计（至少 ${minQty}）`
          : `计划数量不可小于已采购净用量（已采购 ${minQty}）`,
        { type: 'error' },
      );
      return;
    }
    setSaving(true);
    try {
      const res = await updatePurchaseDepartmentPlanQuantity({
        id,
        quantity: displayQty,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '修改失败', { type: 'error' });
        return;
      }
      showToast(res.message || '计划数量已更新', { type: 'success' });
      setEditingId('');
      setReloadKey((key) => key + 1);
      onChanged?.();
    } catch {
      showToast('网络异常，修改失败', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const id = pendingDelete ? itemKey(pendingDelete) : '';
    if (!id || deleting) return;
    setDeleting(true);
    try {
      const res = await deletePurchaseDepartmentPlanItem(id);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除失败', { type: 'error' });
        return;
      }
      showToast(res.message || '计划明细已删除', { type: 'success' });
      if (editingId === id) setEditingId('');
      setPendingDelete(null);
      setReloadKey((key) => key + 1);
      onChanged?.();
    } catch {
      showToast('网络异常，删除失败', { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const submittedGroups = groups.filter((group) => (group.items?.length ?? 0) > 0);
  const unsubmittedGroups = groups.filter((group) => (group.items?.length ?? 0) === 0);
  const tableGroups = [
    ...submittedGroups,
    ...(hospital ? [hospital] : []),
    ...unsubmittedGroups,
  ];
  const rows = tableGroups.flatMap((group) => {
    const items = group.items && group.items.length > 0 ? group.items : [null];
    return items.map((item, index) => ({
      group,
      item,
      index,
      span: items.length,
    }));
  });

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl w-full max-w-5xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-background-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground-900">采购计划明细</h3>
            <p className="text-xs text-foreground-500 mt-1 truncate">
              {[monthLabel, goodsTitle, specLabel].filter(Boolean).join(' · ') || '计划明细'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
            title="关闭"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-16 text-center text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-lg mr-1.5" />
              正在加载科室明细...
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          ) : tableGroups.length === 0 ? (
            <p className="py-12 text-center text-sm text-foreground-400">该物资暂无全院、科室采购计划或采购记录</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-background-50">
                    <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      科室
                    </th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      计划提交数量
                    </th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      科室已采购数量
                    </th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      状态
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      备注
                    </th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap w-24">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ group, item, index, span }) => {
                    const isHospital = !!group.is_hospital || n(group.control_type) === 1;
                    const deptKey = String(group.department_id ?? group.department_name ?? index);
                    if (!item) {
                      return (
                        <tr key={`dept-${deptKey}`} className="hover:bg-background-50/60">
                          <td className="py-2.5 px-3 text-xs text-foreground-800 whitespace-nowrap">
                            {group.department_name || '未命名科室'}
                          </td>
                          <td className="py-2.5 px-3 text-center text-xs text-foreground-400">0</td>
                          <td className="py-2.5 px-3 text-center text-xs font-medium text-foreground-800">
                            {n(group.purchased_quantity)}
                            {qtySuffix}
                          </td>
                          <td className="py-2.5 px-3 text-center text-xs text-foreground-400">-</td>
                          <td className="py-2.5 px-3 text-xs text-foreground-400">尚无科室计划</td>
                          <td className="py-2.5 px-3" />
                        </tr>
                      );
                    }

                    const id = itemKey(item);
                    const editing = editingId === id;
                    const ratio = Math.max(1, n(item.multiple_unit));
                    const used = n(item.net_used_quantity);
                    const cannotDelete = item.deletable === false || (item.deletable == null && used > 0);
                    const tag = getFlowStatusTag(item.flow_status, item.flow_status_text || undefined);
                    const multi = span > 1;
                    return (
                      <tr
                        key={id || `${deptKey}-${index}`}
                        className={isHospital ? 'bg-primary-50/50' : 'hover:bg-background-50/60'}
                      >
                        {index === 0 && (
                          <td
                            rowSpan={span}
                            className="py-2.5 px-3 text-xs text-foreground-800 align-middle whitespace-nowrap"
                          >
                            <div className={isHospital ? 'font-medium text-primary-700' : ''}>
                              {isHospital ? '全院' : group.department_name || '未命名科室'}
                            </div>
                            {isHospital || multi ? (
                              <div className="text-[11px] text-foreground-400 mt-0.5">
                                {isHospital ? '全院计划' : `合计 ${n(group.plan_quantity)}${qtySuffix}`}
                                {isHospital && multi
                                  ? ` · 合计 ${n(group.plan_quantity)}${qtySuffix}`
                                  : ''}
                              </div>
                            ) : null}
                          </td>
                        )}
                        <td className="py-2.5 px-3 text-center text-xs whitespace-nowrap">
                          {editing ? (
                            <input
                              type="number"
                              min={1}
                              max={99999}
                              value={editQty}
                              disabled={saving}
                              onChange={(e) => {
                                const next = Number(e.target.value);
                                setEditQty(Number.isNaN(next) ? 0 : next);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void saveEdit(item);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  if (!saving) setEditingId('');
                                }
                              }}
                              className="w-20 mx-auto text-center text-xs border border-primary-300 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
                            />
                          ) : (
                            <span className="font-medium text-foreground-800">
                              {n(item.plan_quantity)}
                              {qtySuffix}
                            </span>
                          )}
                          {item.folded ? (
                            <div className="text-[11px] text-foreground-400 mt-0.5">
                              原 {n(item.quantity)}
                              {item.unit || ''} × {ratio}
                            </div>
                          ) : null}
                          {!isHospital && used > 0 ? (
                            <div className="text-[11px] text-foreground-400 mt-0.5">已占用 {used}</div>
                          ) : null}
                        </td>
                        {index === 0 && (
                          <td
                            rowSpan={span}
                            className="py-2.5 px-3 text-center text-xs font-medium text-foreground-800 align-middle whitespace-nowrap"
                          >
                            <div>
                              {n(group.purchased_quantity)}
                              {qtySuffix}
                            </div>
                            {isHospital ? (
                              <div className="text-[11px] text-foreground-400 mt-0.5 font-normal">
                                使用数量
                              </div>
                            ) : null}
                          </td>
                        )}
                        <td className="py-2.5 px-3 text-center whitespace-nowrap">
                          <span
                            className="inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium text-white"
                            style={{ backgroundColor: tag.color }}
                          >
                            {tag.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-xs text-foreground-500 max-w-[180px] truncate">
                          {item.remark || '-'}
                        </td>
                        <td className="py-2.5 px-3 text-center whitespace-nowrap">
                          {editing ? (
                            <div className="inline-flex items-center justify-center gap-1">
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => void saveEdit(item)}
                                title="保存"
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                              >
                                <i
                                  className={`text-sm ${
                                    saving ? 'ri-loader-4-line animate-spin' : 'ri-check-line'
                                  }`}
                                />
                              </button>
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => setEditingId('')}
                                title="取消"
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-500 hover:bg-background-100 disabled:opacity-50"
                              >
                                <i className="ri-close-line text-sm" />
                              </button>
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center gap-1">
                              <button
                                type="button"
                                disabled={busy || !id}
                                onClick={() => startEdit(item)}
                                title="修改计划数量"
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-40"
                              >
                                <i className="ri-edit-line text-sm" />
                              </button>
                              <button
                                type="button"
                                disabled={busy || !id || cannotDelete}
                                onClick={() => setPendingDelete(item)}
                                title={
                                  cannotDelete
                                    ? isHospital
                                      ? '已有科室采购，无法删除'
                                      : '已产生采购，无法删除'
                                    : '删除计划明细'
                                }
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-background-50">
                    <td className="py-2 px-3 text-right text-xs font-medium text-foreground-700">合计</td>
                    <td className="py-2 px-3 text-center text-xs font-bold text-foreground-900">
                      {totalPlan}
                      {qtySuffix}
                    </td>
                    <td className="py-2 px-3 text-center text-xs font-bold text-foreground-900">
                      {totalPurchased}
                      {qtySuffix}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-background-200 flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-50"
          >
            关闭
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!pendingDelete}
        zIndex={140}
        title="删除计划明细"
        message={
          n(pendingDelete?.control_type) === 1
            ? `确认删除「${goodsTitle || '该物资'}」的这条全院计划明细？删除后不可恢复。`
            : `确认删除「${goodsTitle || '该物资'}」的这条科室计划明细？删除后不可恢复。`
        }
        confirmLabel="确认删除"
        danger
        loading={deleting}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
